// Lib
import { useEffect, useState } from 'react';
import { isNil } from 'lodash';
import { useSelector } from 'react-redux';

// Utils
import { initialiseSnapPoints } from '../utils/snapPointUtils';
import {
    getSheetHeightAdjustedForClose,
    setContentHeightToMax,
    setContentHeightToVisibleSpace,
    setSheetHeight,
    setSheetHeightVariable,
} from '../utils/sheetHeightUtils';
import runOnTransitionEndWithTimeout from '../../../../utils/dom/runOnTransitionEndWithTimeout';
import { getPlatformDetailsSelector } from '../../../../platform/platformSelector';
import { shouldUseNativeAndroidKeyboardResize } from '../../../../platform/utils/platformDetailsUtils';

// Types
import { SheetProps } from '../SheetContainer';

type SheetSnapPointState = {
    snapPointsState: number[];
    addSnapPoint: (snapPoint: number) => void;
};

/**
 * Take care of snapPoint and activeSnapPoint state, and expose functions to update it
 */
const useSheetSnapPointState = (sheetProps: SheetProps): SheetSnapPointState => {
    const { sheetContainerRef, sheetRef, snapPoints = [], activeSnapPoint, preventDismiss = false } = sheetProps;
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const useNativeAndroidKeyboardResize = shouldUseNativeAndroidKeyboardResize(platformDetails);

    const [snapPointsState, setSnapPointsState] = useState<number[]>(initialiseSnapPoints(snapPoints, preventDismiss));

    const addSnapPoint = (snapPoint: number) => {
        if (snapPointsState.includes(snapPoint)) return;

        setSnapPointsState([...snapPointsState, snapPoint].sort());
    };

    useEffect(() => {
        sheetRef.current?.classList.add('sheet-transition');

        const newSheetHeight = getSheetHeightAdjustedForClose(activeSnapPoint);
        const newHeight = setSheetHeight(sheetRef, newSheetHeight);

        if (isNil(newHeight)) {
            sheetRef.current?.classList.remove('sheet-transition');
            return;
        }

        sheetRef.current?.classList.toggle('sheet-covers-screen', newSheetHeight === window.innerHeight);
        setContentHeightToMax(sheetProps);
        setSheetHeightVariable(sheetContainerRef, newHeight);

        const cancelRunCallback = runOnTransitionEndWithTimeout(sheetRef, () => {
            setContentHeightToVisibleSpace(sheetProps, useNativeAndroidKeyboardResize);
            sheetRef.current?.classList.remove('sheet-transition');
        });

        return cancelRunCallback;
    }, [activeSnapPoint, useNativeAndroidKeyboardResize]);

    useEffect(() => {
        if (!useNativeAndroidKeyboardResize || activeSnapPoint === 0) return;

        const updateSheetLayoutForViewportResize = () => {
            const newSheetHeight = getSheetHeightAdjustedForClose(activeSnapPoint);
            const newHeight = setSheetHeight(sheetRef, newSheetHeight);
            if (isNil(newHeight)) return;

            sheetRef.current?.classList.toggle('sheet-covers-screen', newSheetHeight === window.innerHeight);
            setSheetHeightVariable(sheetContainerRef, newHeight);
            setContentHeightToVisibleSpace(sheetProps, true);
        };

        updateSheetLayoutForViewportResize();

        window.visualViewport?.addEventListener('resize', updateSheetLayoutForViewportResize);

        return () => {
            window.visualViewport?.removeEventListener('resize', updateSheetLayoutForViewportResize);
        };
    }, [activeSnapPoint, useNativeAndroidKeyboardResize]);

    return { snapPointsState, addSnapPoint };
};

export default useSheetSnapPointState;
